type LoadMoreProps = {
    loading: boolean;
    hasNext: boolean;
    page: number;
    size: number;
    totalElements: number;
    currentCount: number;
    setPage: React.Dispatch<React.SetStateAction<number>>;
    /** "light" = white/gray/indigo (default, used on light pages); "dark" = slate/cyan (recruiter UI) */
    variant?: "light" | "dark";
  };
  
  const LoadMoreButton = ({
    loading,
    hasNext,
    page,
    size,
    totalElements,
    currentCount,
    setPage,
    variant = "light",
  }: LoadMoreProps) => {
    const isDark = variant === "dark";
    // Showing 1 to 2 of 14
    const start = totalElements === 0 ? 0 : 1;
  
    const end = Math.min(currentCount, totalElements);
  
    return (
      <div className="w-full flex flex-col items-center gap-4 mt-10">
        {/* Result Count */}
        <div
          className={`text-sm sm:text-base font-medium text-center ${
            isDark ? "text-slate-400" : "text-gray-500"
          }`}
        >
          Showing{" "}
          <span className={`font-semibold ${isDark ? "text-slate-100" : "text-gray-900"}`}>
            {start}
          </span>{" "}
          to{" "}
          <span className={`font-semibold ${isDark ? "text-slate-100" : "text-gray-900"}`}>
            {end}
          </span>{" "}
          of{" "}
          <span className={`font-semibold ${isDark ? "text-cyan-400" : "text-indigo-600"}`}>
            {totalElements}
          </span>{" "}
          results
        </div>
  
        {/* Button */}
        {hasNext ? (
          <button
            disabled={loading}
            onClick={() => setPage(page + 1)}
            className={`
              group
              relative
              overflow-hidden
              px-6 sm:px-8
              py-3
              rounded-2xl
              font-semibold
              text-sm sm:text-base
              shadow-lg
              transition-all duration-300
              hover:scale-[1.02]
              hover:shadow-xl
              active:scale-[0.98]
              disabled:opacity-50
              disabled:cursor-not-allowed
              min-w-[180px]
              ${
                isDark
                  ? "bg-cyan-500 text-slate-950 shadow-cyan-500/20 hover:shadow-cyan-500/30"
                  : "bg-indigo-600 text-white shadow-indigo-500/20 hover:shadow-indigo-500/30"
              }
            `
            }
          >
            {/* Glow */}
            <span className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
  
            <span className="relative flex items-center justify-center gap-2">
              {loading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
  
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    />
                  </svg>
  
                  Loading...
                </>
              ) : (
                <>
                  Load More
  
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="
                      h-4 w-4
                      transition-transform
                      duration-300
                      group-hover:translate-y-0.5
                    "
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 14l-7 7m0 0l-7-7m7 7V3"
                    />
                  </svg>
                </>
              )}
            </span>
          </button>
        ) : (
          <div
            className={`px-6 py-3 rounded-2xl backdrop-blur-sm text-sm font-medium shadow-sm ${
              isDark
                ? "bg-slate-800/60 border border-slate-700 text-slate-500"
                : "bg-white/70 border border-gray-200 text-gray-500"
            }`}
          >
            🎉 You’ve reached the end
          </div>
        )}
      </div>
    );
  };
  
  export default LoadMoreButton;